import axios, { AxiosResponse } from 'axios';

// API基础配置
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 401 → /login redirect (session expired / not authed)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (!path.startsWith('/login')) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/login?next=${next}&error=session_expired`);
      }
    }
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// 创建一个类型化的API客户端
const api = {
  get: <T>(url: string, config?: any): Promise<T> => {
    return apiClient.get(url, config);
  },
  post: <T>(url: string, data?: any, config?: any): Promise<T> => {
    return apiClient.post(url, data, config);
  },
  put: <T>(url: string, data?: any, config?: any): Promise<T> => {
    return apiClient.put(url, data, config);
  },
  delete: <T>(url: string, config?: any): Promise<T> => {
    return apiClient.delete(url, config);
  },
};

// 接口类型定义
export interface DashboardStats {
  activeUsers: number;        // 活跃用户数：统计日期选择器选择的日期，日志中出现的用户数
  paidUsers: number;          // 有料用户数：最新的有料用户数（与选择日期无关）
  newUsers: number;           // 新规用户数：当日的新注册用户数
  totalUsers: number;         // 总用户数：业务数据库的总用户数（与选择日期无关）
  totalSessions: number;      // 总会话数
  avgSessionDuration: number; // 平均会话时长
  planDistribution: Record<string, number>; // 活跃用户计划分布
  topPages: Array<{
    pageName: string;
    pageType: string;
    visitCount: number;
    uniqueUsers: number;
    avgDuration: number;
  }>;
  last30DaysTrend: Array<{
    date: string;
    activeUsers: number;
    paidUsers: number;
    totalDuration: number;
  }>;
}

export interface UserBehavior {
  user_id: string;
  user_name: string;           // 用户姓名等基础信息
  vip_status: number;          // 用户计划（VIP状态）
  session_count: number;       // 会话数：超过20分钟没操作记为1次会话
  total_duration: number;      // 总时间(分)：当日所有会话时间加一起
  activity_count: number;      // 活动回数：所有映射的活动的条目总数
  pages_visited: Record<string, number>; // 常用页面：PAGE_TYPES 2到8的7个页面浏览次数
  first_activity: string;      // 最初活动：第一条日志时间
  last_activity: string;       // 最后活动：最后一条日志时间
  unique_pages: number;        // 访问的唯一页面数
  // 新增基础信息字段
  clinic?: string;             // 诊所名称
  prefecture?: string;         // 地区/都道府县
  specialty?: string;          // 专业/专科
  email?: string;              // 邮箱地址
}

export interface UserBehaviorResponse {
  date: string;
  total_users: number;
  page: number;
  limit: number;
  users: UserBehavior[];
}

export interface AvailableDatesResponse {
  available_dates: string[];
  latest_date: string;
  total_dates: number;
}

export interface TrendData {
  trends: Array<{
    date: string;
    activeUsers: number;
    paidUsers: number;
    totalDuration: number;
  }>;
  period: string;
  end_date: string;
}

export interface PaidUsersGrowthData {
  growthData: Array<{
    date: string;
    newPaidUsers: number;
    cumulativePaidUsers: number;
  }>;
  period: string;
  end_date: string;
  total_new_paid_users: number;
  final_cumulative_count: number;
}

export interface NewUsersGrowthData {
  growthData: Array<{
    date: string;
    newUsers: number;
  }>;
  period: string;
  end_date: string;
  total_new_users: number;
  average_new_users: number;
  max_new_users: number;
  currentTotalUsers: number;
}

export interface RegionDistributionData {
  date: string;
  regions: Array<{
    name: string;
    value: number;
  }>;
  total_users: number;
}

// API方法
export const analyticsApi = {
  // 获取仪表板统计数据
  getDashboardStats: async (date: string): Promise<DashboardStats> => {
    return api.get<DashboardStats>(`/api/analytics/dashboard-stats/${date}`);
  },

  // 获取用户行为数据
  getUserBehaviors: async (
    date: string,
    page: number = 1,
    limit: number = 20,
    vipStatus?: string,
    minDuration?: number
  ): Promise<UserBehaviorResponse> => {
    const params: any = { page, limit };
    if (vipStatus) params.vip_status = vipStatus;
    if (minDuration) params.min_duration = minDuration;

    return api.get<UserBehaviorResponse>(`/api/analytics/user-behaviors/${date}`, { params });
  },

  // 获取可用日期
  getAvailableDates: async (): Promise<AvailableDatesResponse> => {
    return api.get<AvailableDatesResponse>('/api/analytics/available-dates');
  },

  // 获取趋势数据
  getTrends: async (days: number = 30, endDate?: string): Promise<TrendData> => {
    const params: any = { days };
    if (endDate) params.end_date = endDate;

    return api.get<TrendData>('/api/analytics/trends', { params });
  },

  // 获取用户详情
  getUserDetail: async (userId: string, startDate: string, endDate: string) => {
    return api.get<any>(`/api/analytics/user/${userId}`, {
      params: { start_date: startDate, end_date: endDate }
    });
  },

  // 检查系统健康状态
  getSystemHealth: async () => {
    return api.get<any>('/health');
  },

  // 获取活跃用户排名
  getActiveUsersRanking: async () => {
    return api.get<any>('/api/analytics/active-users-ranking');
  },

  // 获取PRO用户列表
  getProUsers: async () => {
    return api.get<any>('/api/analytics/pro-users');
  },

  // 获取新用户列表
  getNewUsers: async (date: string) => {
    return api.get<any>(`/api/analytics/new-users/${date}`);
  },

  // 获取有料用户增长趋势
  getPaidUsersGrowth: async (days: number = 30, endDate?: string): Promise<PaidUsersGrowthData> => {
    const params: any = { days };
    if (endDate) params.end_date = endDate;

    return api.get<PaidUsersGrowthData>('/api/analytics/paid-users-growth', { params });
  },

  // 获取新规用户增长趋势
  getNewUsersGrowth: async (days: number = 30, endDate?: string): Promise<NewUsersGrowthData> => {
    const params: any = { days };
    if (endDate) params.end_date = endDate;

    return api.get<NewUsersGrowthData>('/api/analytics/new-users-growth', { params });
  },

  // 获取用户地区分布数据
  getUserRegionDistribution: async (date: string): Promise<RegionDistributionData> => {
    return api.get<RegionDistributionData>(`/api/analytics/${date}/user-region-distribution`);
  },

  // 获取用户专业分布数据
  getUserSpecialtyDistribution: async (date: string): Promise<any> => {
    return api.get<any>(`/api/analytics/${date}/user-specialty-distribution`);
  },

  // 检查Google Analytics可用性
  checkGoogleAnalyticsAvailability: async (): Promise<any> => {
    return api.get<any>('/api/analytics/google-analytics/availability');
  },

  // 获取用户获取数据（Google Analytics）
  getUserAcquisitionData: async (date: string): Promise<any> => {
    return api.get<any>(`/api/analytics/google-analytics/user-acquisition?date=${date}`);
  },

  // 获取渠道组分布数据（Google Analytics）
  getChannelGroupDistribution: async (startDate?: string, endDate?: string): Promise<any> => {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    return api.get<any>('/api/analytics/google-analytics/channel-distribution', { params });
  },

  // 获取用户获取趋势（Google Analytics）
  getUserAcquisitionTrends: async (days: number = 30): Promise<any> => {
    return api.get<any>(`/api/analytics/google-analytics/acquisition-trends?days=${days}`);
  },

  // 导出用户数据报表
  exportUsers: async (startDate: string, endDate: string): Promise<Blob> => {
    return apiClient.post("/api/analytics/export-users", { start_date: startDate, end_date: endDate }, {
      responseType: "blob",
    });
  },
};

export default apiClient;
